import {Component, OnDestroy, OnInit} from '@angular/core';
import {ISetting} from '../../../shared/model';
import {Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {GeneralSettingService} from '../general-setting.service';
import {HttpResponse} from '@angular/common/http';
import {EventManager} from '../../../core/services';
import {NzNotificationService} from 'ng-zorro-antd/notification';

@Component({
  selector: 'app-general-setting-list',
  templateUrl: './general-setting-list.component.html',
  styleUrls: ['./general-setting-list.component.scss']
})
export class GeneralSettingListComponent implements OnInit, OnDestroy {
  settings?: ISetting[];
  eventSubscriber?: Subscription;

  // Setting variable
  twilioAccount: string;
  twilioAuthToken: string;
  twilioPhone: string;
  digitalCurrencyPriceApiUrl: string;
  digitalCurrencyPriceApiKey: string;
  makerFee: string;

  // Digital configuration
  bitcoinUrl: string;
  bitcoinPrefix: string;
  bitcoinKey: string;
  ltcUrl: string;
  etherscanUrl: string;
  etherscanKey: string;

  // Reset time
  resetTime: string;
  waitingCancelTime: string;
  withdrawalCancelTime: string;
  verifyEmailTime: string;
  transactionCancelTime: string;
  jobPeriodTime: string;

  constructor(
    protected generalSettingService: GeneralSettingService,
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected eventManager: EventManager,
    private notification: NzNotificationService
  ) {
  }

  loadPage(): void {
    this.generalSettingService
      .queryNoPaging({
        sort: ['id,asc']
      })
      .subscribe(
        (res: HttpResponse<ISetting[]>) => this.onSuccess(res.body),
        () => this.onError()
      );
  }

  ngOnInit(): void {
    this.loadPage();
    this.registerChangeInSettings();
  }

  ngOnDestroy(): void {
    if (this.eventSubscriber) {
      this.eventManager.destroy(this.eventSubscriber);
    }
  }

  trackId(index: number, item: ISetting): number {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    return item.id;
  }

  registerChangeInSettings(): void {
    this.eventSubscriber = this.eventManager.subscribe('settingListModification', () => this.loadPage());
  }

  protected onSuccess(data: ISetting[]): void {
    this.settings = data || [];
    if (data && data.length > 0) {
      this.twilioAccount = data[0].val;
      this.twilioAuthToken = data[1].val;
      this.twilioPhone = data[2].val;
      this.digitalCurrencyPriceApiUrl = data[3].val;
      this.digitalCurrencyPriceApiKey = data[4].val;
      this.makerFee = data[5].val;
      this.bitcoinUrl = data[6].val;
      this.bitcoinPrefix = data[7].val;
      this.bitcoinKey = data[8].val;
      this.ltcUrl = data[9].val;
      this.etherscanKey = data[10].val;
      this.etherscanUrl = data[11].val;
      this.resetTime = data[12].val;
      this.waitingCancelTime = data[13].val;
      this.withdrawalCancelTime = data[14].val;
      this.verifyEmailTime = data[15].val;
      this.transactionCancelTime = data[16].val;
      this.jobPeriodTime = data[17].val;
    }
  }

  protected onError(): void {
    this.notification.create(
      'error',
      'Error',
      'Error incurred. Please try again!'
    );
  }

  save() {
    const updateItem = [];
    this.settings.forEach((e, index) => {
      if (index === 0 && e.val !== this.twilioAccount) {
        e.val = this.twilioAccount;
        updateItem.push(e);
      }

      if (index === 1 && e.val !== this.twilioAuthToken) {
        e.val = this.twilioAuthToken;
        updateItem.push(e);
      }

      if (index === 2 && e.val !== this.twilioPhone) {
        e.val = this.twilioPhone;
        updateItem.push(e);
      }

      if (index === 3 && e.val !== this.digitalCurrencyPriceApiUrl) {
        e.val = this.digitalCurrencyPriceApiUrl;
        updateItem.push(e);
      }

      if (index === 4 && e.val !== this.digitalCurrencyPriceApiKey) {
        e.val = this.digitalCurrencyPriceApiKey;
        updateItem.push(e);
      }

      if (index === 5 && e.val !== this.makerFee) {
        e.val = this.makerFee;
        updateItem.push(e);
      }

      if (index === 6 && e.val !== this.bitcoinUrl) {
        e.val = this.bitcoinUrl;
        updateItem.push(e);
      }

      if (index === 7 && e.val !== this.bitcoinPrefix) {
        e.val = this.bitcoinPrefix;
        updateItem.push(e);
      }

      if (index === 8 && e.val !== this.bitcoinKey) {
        e.val = this.bitcoinKey;
        updateItem.push(e);
      }

      if (index === 9 && e.val !== this.ltcUrl) {
        e.val = this.ltcUrl;
        updateItem.push(e);
      }

      if (index === 10 && e.val !== this.etherscanKey) {
        e.val = this.etherscanKey;
        updateItem.push(e);
      }

      if (index === 11 && e.val !== this.etherscanUrl) {
        e.val = this.etherscanUrl;
        updateItem.push(e);
      }

      if (index === 12 && e.val !== this.resetTime) {
        e.val = this.resetTime;
        updateItem.push(e);
      }

      if (index === 13 && e.val !== this.waitingCancelTime) {
        e.val = this.waitingCancelTime;
        updateItem.push(e);
      }

      if (index === 14 && e.val !== this.withdrawalCancelTime) {
        e.val = this.withdrawalCancelTime;
        updateItem.push(e);
      }

      if (index === 15 && e.val !== this.verifyEmailTime) {
        e.val = this.verifyEmailTime;
        updateItem.push(e);
      }

      if (index === 16 && e.val !== this.transactionCancelTime) {
        e.val = this.transactionCancelTime;
        updateItem.push(e);
      }

      if (index === 17 && e.val !== this.jobPeriodTime) {
        e.val = this.jobPeriodTime;
        updateItem.push(e);
      }
    });

    updateItem.forEach((e, index) => {
      this.saveItemSetting(e, index, updateItem.length - 1);
    });
  }

  saveItemSetting(setting: ISetting, index: number, finishIndex: number) {
    this.generalSettingService
      .update(setting)
      .subscribe(
        (res: HttpResponse<ISetting>) => {
          if (index === finishIndex) {
            this.notification.create(
              'success',
              'Done',
              'Successfully saved!'
            );
          }
        },
          () => this.onError()
        );
  }
}
